import UiInput from './ui/Input';
import FieldLabel from './ui/FieldLabel';
import DateInput from './ui/DateInput';
import React from "react";
import DatePicker from "react-datepicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import "react-datepicker/dist/react-datepicker.css";
const ExtendedThursdayDateInput = React.forwardRef(({ value, className = '', ...props }, ref) => (
    <UiInput className={`uiDateInput ${className}`}
        {...props}
        ref={ref}
        value={String(value || '').replace(/^Thu,/, 'Thur,')}
    />
))

const StackedDateTimeInput = React.forwardRef(({ value, onClick, disabled, className }, ref) => {
    const [datePart = '', timePart = ''] = String(value || '').split('|')
    return (
        <button
            type="button"
            ref={ref}
            className={`uiField uiDateInput stackedDateTimeInput ${className || ''}`.trim()}
            disabled={disabled}
            onClick={onClick}
            aria-label="Choose date and time"
        >
            <span>{datePart.replace(/^Thu,/, 'Thur,')}</span>
            <span>{timePart}</span>
        </button>
    )
})

const MyCalendar = ({
    startDate,
    setStartDate,
    showYearDropdown,
    showMonthDropdown,
    dropdownMode,
    todayButton,
    disabled,
    label,
    storeString,
    minDate,
    noMaxDate,
    showTimeOnly,
    showDateOnly,
    required,
    placeholder,
    timeIntervals = 1,
    calendarStartDay = 1,
    dateFormat,
    showMonthYearPicker,
    showYearPicker,
    stackDateAndTime,
    calendarClassName,
    useAppCalendarPicker = false,
    hideLabel = false,
    nativeOnMobile = false,
}) => {
    const inputId = React.useId()
    const [isMobile, setIsMobile] = React.useState(() => window.matchMedia?.('(max-width: 700px)').matches || false)
    React.useEffect(() => {
        const media = window.matchMedia?.('(max-width: 700px)')
        if (!media) return
        const update = () => setIsMobile(media.matches)
        media.addEventListener?.('change', update)
        return () => media.removeEventListener?.('change', update)
    }, [])
    const datePickerRef = React.useRef(null)
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const resolvedCalendarClassName = [
        calendarClassName || 'timesheetCompactDateTimePicker',
        useAppCalendarPicker ? 'appCalendarPicker' : '',
        useAppCalendarPicker ? (showDateOnly ? 'appCalendarPickerDateOnly' : 'appCalendarPickerDateTime') : '',
    ].filter(Boolean).join(' ')

    const maxDate = noMaxDate ? {} : { maxDate: new Date() }
    const resolvedMinDate = minDate ? (minDate instanceof Date ? minDate : new Date(minDate)) : null
    const minDateObj = resolvedMinDate ? { minDate: resolvedMinDate } : {}

    const closePicker = (force = false) => {
        if (!force && !(showDateOnly || showMonthYearPicker || showYearPicker)) return
        setPickerOpen(false)
        datePickerRef.current?.setOpen(false)
        datePickerRef.current?.input?.blur?.()
        window.setTimeout(() => {
            datePickerRef.current?.setOpen(false)
            datePickerRef.current?.input?.blur?.()
        }, 0)
    }

    const handleChange = (date, event) => {
        setStartDate(storeString ? (date ? date.toISOString() : '') : date)

        const selectedTime = event?.target?.closest?.('.react-datepicker__time-list-item')
        if (showDateOnly || showTimeOnly || selectedTime) {
            closePicker(true)
        }
    }

    // Local date/time preserves the worker's timezone; ISO formatting would shift the displayed hours.
    if (nativeOnMobile && isMobile && !showDateOnly && !showTimeOnly) {
        const selected = startDate ? new Date(startDate) : null
        const pad = value => String(value).padStart(2, '0')
        const value = selected && !Number.isNaN(selected.getTime())
            ? `${selected.getFullYear()}-${pad(selected.getMonth() + 1)}-${pad(selected.getDate())}T${pad(selected.getHours())}:${pad(selected.getMinutes())}` : ''
        return <div className='workerMobileDateTimeField'>
            <FieldLabel className='fieldLabel' htmlFor={inputId}>{label || 'Date & Time'}</FieldLabel>
            <DateInput id={inputId} type='datetime-local' disabled={!!disabled} required={required} value={value}
                onChange={event => {
                    const date = event.target.value ? new Date(event.target.value) : null
                    if (!date || !Number.isNaN(date.getTime())) setStartDate(storeString ? (date ? date.toISOString() : '') : date)
                }} />
        </div>
    }

    return (<div className="flex-wrap">
        {!hideLabel && <FieldLabel className="fieldLabel">{label || 'Date & Time'}{required && <span className="uiRequired" style={{ color: '#dc2626' }}>*</span>}</FieldLabel>}
        <DatePicker
            ref={datePickerRef}
            open={pickerOpen}
            showIcon
            toggleCalendarOnIconClick
            icon={showTimeOnly ? <AccessTimeOutlinedIcon /> : <CalendarTodayOutlinedIcon />}
            disabled={disabled}
            showYearDropdown={showYearDropdown}
            showMonthDropdown={showMonthDropdown}
            dropdownMode={dropdownMode}
            scrollableYearDropdown
            yearDropdownItemNumber={30}
            selected={startDate ? (typeof (startDate) === 'string' ? new Date(startDate) : startDate) : startDate}
            {...minDateObj}
            {...maxDate}
            dateFormat={dateFormat || (showDateOnly ? 'EEE, d MMM yyyy' : showTimeOnly ? 'HH:mm a' : stackDateAndTime ? "EEE, d MMM yyyy'|'h:mm a" : 'EEE, d MMM yyyy HH:mm')}
            customInput={stackDateAndTime ? <StackedDateTimeInput /> : <ExtendedThursdayDateInput />}
            calendarClassName={resolvedCalendarClassName}
            todayButton={todayButton}
            showMonthYearPicker={!!showMonthYearPicker}
            showYearPicker={!!showYearPicker}
            shouldCloseOnSelect={!!(showDateOnly || showMonthYearPicker || showYearPicker)}
            showTimeSelect={!showDateOnly}
            showTimeSelectOnly={!!showTimeOnly}
            timeFormat='HH:mm'
            timeIntervals={timeIntervals}
            calendarStartDay={calendarStartDay}
            placeholderText={placeholder}
            portalId="datepicker-portal"
            onInputClick={() => setPickerOpen(true)}
            onClickOutside={() => setPickerOpen(false)}
            onCalendarClose={() => setPickerOpen(false)}
            onKeyDown={(event) => { if (event.key === 'Escape') setPickerOpen(false) }}
            onSelect={() => closePicker()}
            onChange={handleChange}
        />
    </div>);
}

export default MyCalendar
